import { defineStore } from 'pinia'
import { getUrlQueryParams } from '@/utils/getUrlQueryParams'
import { useDocumentStore } from '@/stores/document'
import API from '@/api/apiUrls'
import type {
	NewShipType,
	PaginationServer,
	NewShipKey,
	ShipType,
	DefaulShipField
} from '@/stores/type/ship.type'
import { useAuthenticationStore } from '@/stores/authentication'
import { useApi } from '@/api/useApi'
import { useBaseStore } from '@haskoning/vue-basestore'
import { computed, ref } from 'vue'

const baseUrl = API.SHIP.BASE
const paginateUrl = API.SHIP.PAGINATE

const api = useApi<ShipType>()

const defaultPaginationState: PaginationServer<ShipType> = {
	count: 0,
	next: null,
	previous: null,
	results: []
}

export const useShipStore = defineStore('ship', () => {
	const baseStore = useBaseStore<ShipType>({ service: baseUrl, api })

	const newItem = ref<Omit<ShipType, 'id'> | null>(null)

	const createShipDialog = ref(false)
	const downloadTemplateLoading = ref(false)
	const downloadTemplateDialog = ref(false)
	const createFullShipDialog = ref(false)
	const previewFullShipDialog = ref(false)
	const documentDialog = ref(false)
	const exportExcelDialog = ref(false)
	const exportAutoCADDialog = ref(false)
	const exportExcelLoading = ref(false)
	const exportAutoCADLoading = ref(false)
	const previewDocumentDialog = ref(false)
	const uploadLoading = ref(false)
	const importSuccess = ref(false)
	const importLoading = ref(false)
	const menu = ref(false)
	const menuCoordinates = ref({ x: 0, y: 0 })
	const page = ref(1)

	const importItem = ref<ShipType | null>(null)
	const filter = ref({})
	const paginationServer = ref(defaultPaginationState)

	const baseShipFieldKeys: DefaulShipField[] = [
		'ship_class',
		'capacity',
		'length_overall',
		'breadth',
		'depth_main_deck_to_keel',
		'laden_draught',
		'ballasted_draught',
		'forward_parallel_body_length',
		'aftward_parallel_body_length'
	]

	const armourFieldKeys: DefaulShipField[] = [
		'main_engine_power',
		'main_engine_diameter',
		'main_engine_distance_between',
		'main_engine_distance_tip_keel',
		'main_engine_distance_ship_stern',
		'transverse_thruster_power',
		'transverse_thruster_diameter',
		'transverse_thruster_distance_between',
		'transverse_thruster_distance_tip_keel',
		'transverse_thruster_duct_length',
		'rudder',
		'main_engine_type',
		'transverse_thruster_type',
		'main_engine_number',
		'transverse_thruster_number'
	]

	const baseShipFields = computed(() => {
		const fields = baseStore.fields.value
		if (fields === null) {
			return []
		}
		return Object.keys(fields)
			.filter((key) => baseShipFieldKeys.includes(key as DefaulShipField))
			.map((key) => fields[key])
	})

	const armourFields = computed(() => {
		const fields = baseStore.fields.value
		if (fields === null) {
			return []
		}
		return Object.keys(fields)
			.filter((key) => armourFieldKeys.includes(key as DefaulShipField))
			.map((key) => fields[key])
	})

	const resetState = (): void => {
		baseStore.currentItem.value = null
		baseStore.items.value = []
		page.value = 1
		baseStore.fetchLoading.value = false
		paginationServer.value = defaultPaginationState
	}
	const setFilter = (customFilter: { [key: string]: string | null }): void => {
		filter.value = customFilter
	}
	const setCurrentItem = (item: ShipType | null) => {
		baseStore.currentItem.value = item
		if (item) {
			const documentStore = useDocumentStore()
			documentStore.setDocuments(item.document_set)
		}
	}
	const fetchPaginationItems = async (
		customFilter = {},
		itemsPerPage: number | null = null
	): Promise<ShipType[] | boolean> => {
		baseStore.fetchLoading.value = true
		if (itemsPerPage === null) {
			itemsPerPage = 10
		}
		const filterPagination = {
			...customFilter,
			...filter.value,
			page_size: itemsPerPage
		}
		let url = paginateUrl + getUrlQueryParams(filterPagination)
		if (paginationServer.value.next) {
			url = paginationServer.value.next
		} else {
			if (paginationServer.value.previous) {
				baseStore.fetchLoading.value = false
				return []
			}
		}
		try {
			const response = await api.service(url).get()
			const data = response as unknown as PaginationServer<ShipType>
			baseStore.items.value.push(...data.results)
			paginationServer.value = data
		} catch (e) {
			console.error(e)
			return false
		} finally {
			baseStore.fetchLoading.value = false
		}
		return baseStore.items.value
	}
	const toggleCreateShipDialog = (value: boolean | null = null): void => {
		value = value ?? !createShipDialog.value
		if (value) {
			newItem.value = getEmptyBaseItem.value
		}
		createShipDialog.value = value
	}
	const toggleCreateFullShipDialog = (value: boolean | null = null): void => {
		createFullShipDialog.value = value ?? !createFullShipDialog.value
	}
	const toggleExportExcelDialog = (value: boolean | null = null): void => {
		exportExcelDialog.value = value ?? !exportExcelDialog.value
	}
	const toggleExportAutoCADDialog = (value: boolean | null = null): void => {
		exportAutoCADDialog.value = value ?? !exportAutoCADDialog.value
	}
	const toggleDownloadTemplateDialog = (value: boolean | null = null): void => {
		downloadTemplateDialog.value = value ?? !downloadTemplateDialog.value
	}
	const toggleDocumentDialog = (value: boolean | null = null): void => {
		documentDialog.value = value ?? !documentDialog.value
	}
	const uploadFile = async (formData: FormData): Promise<boolean> => {
		uploadLoading.value = true
		try {
			const url = API.SHIP.UPLOAD
			const response = await api.service(url).post(formData)
			importItem.value = response as ShipType
			uploadLoading.value = false
			return true
		} catch {
			uploadLoading.value = false
			return false
		}
	}
	const importShip = async (): Promise<ShipType | null> => {
		importLoading.value = true
		try {
			const url = API.SHIP.IMPORT
			const formData = new FormData()
			formData.append('ship', JSON.stringify(importItem.value))
			const response = await api.service(url).post(formData)
			importLoading.value = false
			importSuccess.value = true
			return response as ShipType
		} catch (e) {
			console.log(e)
			importLoading.value = false
			throw e
		}
	}
	const toggleMenu = (
		coordinates: { x: number; y: number } | null = null,
		item: ShipType | null = null
	) => {
		if (coordinates) {
			menuCoordinates.value = coordinates
		}
		if (item) {
			baseStore.currentItem.value = item
			menu.value = true
		} else {
			menu.value = !menu.value
		}
	}
	const addDocuments = (item: ShipType): void => {
		const documentStore = useDocumentStore()
		const successItems = documentStore.documentItems.filter((uploadItem) => uploadItem.response)
		successItems.forEach((successItem) => {
			if (successItem.response) {
				item.document_set.push(successItem.response)
			}
		})
	}
	const downloadTemplate = async (): Promise<void> => {
		downloadTemplateLoading.value = true
		const url = API.SHIP.TEMPLATE
		await download(url)
		downloadTemplateLoading.value = false
	}
	const exportExcel = async (id: number): Promise<void> => {
		exportExcelLoading.value = true
		const url = API.SHIP.EXCEL_EXPORT(id)
		await download(url)
		exportExcelLoading.value = false
	}
	const exportAutoCAD = async (id: number): Promise<void> => {
		exportAutoCADLoading.value = true
		const url = API.SHIP.AUTOCAD_EXPORT(id)
		await download(url)
		exportAutoCADLoading.value = false
	}
	const download = async (fileURL: string): Promise<void> => {
		try {
			await api.service(fileURL).downloadBlob()
		} catch (e) {
			console.log(e)
		}
	}

	const createNewShip = (item: NewShipType | null, defaultFields?: DefaulShipField[]) => {
		const newShip = { ...item } as Record<NewShipKey, unknown>
		const defaultFieldsSet = defaultFields ? new Set(defaultFields) : undefined

		baseStore.fieldsList.value.forEach(({ parameter, children, default: defaultValue }) => {
			const parameterAsKey = parameter as DefaulShipField
			if (!parameterAsKey || (defaultFieldsSet && !defaultFieldsSet.has(parameterAsKey))) return
			if (children) {
				newShip[parameterAsKey] = Object.fromEntries(
					Object.entries(children).map(([subField, subFieldData]) => [
						subField,
						subFieldData.default
					])
				)
			} else {
				newShip[parameterAsKey] = defaultValue
			}
		})

		return { ...newShip } as ShipType
	}

	const getEmptyBaseItem = computed(() => {
		return createNewShip({} as ShipType, [...baseShipFieldKeys, 'has_armour_param'])
	})

	const assignArmourDefaultValues = (item: NewShipType): NewShipType => {
		return createNewShip(item, armourFieldKeys) as NewShipType
	}

	const removeArmourValues = (item: NewShipType): NewShipType => {
		const itemCopy = { ...item } as Record<NewShipKey, unknown>
		armourFieldKeys.forEach((key) => {
			delete itemCopy[key as keyof NewShipType]
		})
		return itemCopy as NewShipType
	}

	const deletePermission = () => {
		const isNotApproved = baseStore.currentItem.value?.validation_set.every(
			(approval) => !approval.approved
		)
		const authStore = useAuthenticationStore()
		return (
			authStore.user?.is_superuser ||
			(baseStore.currentItem.value?.user === authStore.user?.id && isNotApproved)
		)
	}
	return {
		...baseStore,
		newItem,
		page,
		filter,
		uploadLoading,
		importItem,
		importSuccess,
		importLoading,
		downloadTemplateLoading,
		menu,
		menuCoordinates,
		baseShipFields,
		armourFields,
		documentDialog,
		previewDocumentDialog,
		createShipDialog,
		previewFullShipDialog,
		downloadTemplateDialog,
		getEmptyBaseItem,
		paginationServer,
		exportExcelDialog,
		exportAutoCADDialog,
		exportExcelLoading,
		exportAutoCADLoading,
		createFullShipDialog,
		resetState,
		setFilter,
		createNewShip,
		assignArmourDefaultValues,
		removeArmourValues,
		setCurrentItem,
		fetchPaginationItems,
		toggleCreateShipDialog,
		toggleCreateFullShipDialog,
		toggleExportExcelDialog,
		toggleExportAutoCADDialog,
		uploadFile,
		importShip,
		toggleMenu,
		addDocuments,
		download,
		downloadTemplate,
		exportExcel,
		exportAutoCAD,
		toggleDownloadTemplateDialog,
		toggleDocumentDialog,
		deletePermission
	}
})
